import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { ToastProvider } from '@wartek-id/toast'
import { historyUtils } from '@wartek-id/fe-toolbox'

import { WithFeatureFlagContext } from 'utils/hooks/context/useFeatureFlagContext'
import {
  WithScreenSizeContext,
  useScreenSizeContext,
} from 'utils/hooks/context/useScreenSizeContext'
import { AuthProvider, ProtectedRoutes } from 'utils/auth'
import { createAndroidAuthDataCallback } from 'utils/androidHandler'
import { Layout as BaseLayout } from 'components/Layout/Layout'
import ErrorBoundary from 'components/ErrorBoundary'
import useTechDebtStoreUserId from 'utils/hooks/useTechDebtStoreUserId'
import { MetaTags } from 'components/MetaTags'

import '@wartek-id/design-tokens/build/guru-mobile/css/color.css'
import '@wartek-id/design-tokens/build/guru-mobile/css/font.css'
import '@wartek-id/design-tokens/build/guru-mobile/css/size.css'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import 'styles/global.css'
import 'react-datepicker/dist/react-datepicker.css'

const STATIC_PAGES = ['/']

createAndroidAuthDataCallback()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
  const mockServer = require('configs/mocks/server').default
  mockServer({ environment: process.env.NODE_ENV })
}

const App = ({ Component, pageProps, router }) => {
  const Layout = Component.Layout ?? BaseLayout
  const { isDesktop: isDesktopScreenSize } = useScreenSizeContext()

  return (
    <Layout {...Component.layoutOpts}>
      <Component
        {...pageProps}
        isDesktopScreenSize={isDesktopScreenSize}
        key={router.route}
      />
    </Layout>
  )
}

const AppProvider = ({ Component, pageProps, router }) => {
  useTechDebtStoreUserId()

  React.useEffect(() => {
    historyUtils.storeAppInitialPath()
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  if (STATIC_PAGES.includes(router.pathname)) {
    return (
      <ToastProvider autoDismiss placement="top-center">
        <QueryClientProvider client={queryClient}>
          <App Component={Component} pageProps={pageProps} router={router} />
        </QueryClientProvider>
      </ToastProvider>
    )
  }

  return (
    <ToastProvider autoDismiss placement="top-center">
      <QueryClientProvider client={queryClient}>
        <MetaTags
          metaTags={pageProps.metaTags}
          pageTitle={pageProps.pageTitle}
          pageDescription={pageProps.pageDescription}
        />
        <AuthProvider>
          <ErrorBoundary resetKeys={[router.asPath]}>
            <ProtectedRoutes router={router}>
              <WithFeatureFlagContext>
                <WithScreenSizeContext>
                  <App
                    Component={Component}
                    pageProps={pageProps}
                    router={router}
                  />
                </WithScreenSizeContext>
              </WithFeatureFlagContext>
            </ProtectedRoutes>
          </ErrorBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </ToastProvider>
  )
}

export default AppProvider
